import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';

const EditSupplierModal = ({ open, onClose, supplier, onSubmit, loading = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState({
    supplier_code: '',
    company_name: '',
    vat_number: '',
    fiscal_code: '',
    address: '',
    city: '',
    postal_code: '',
    province: '',
    phone: '',
    mobile: '',
    email: '',
    pec: '',
    sdi_code: '',
    contact_person: '',
    payment_terms: '',
    payment_days: 30,
    iban: '',
    bank_name: '',
    contract_start_date: null,
    contract_end_date: null,
    current_price_per_liter: '',
    notes: '',
    status: 'active'
  });

  const [error, setError] = useState(null);

  useEffect(() => {
    if (supplier && open) {
      setFormData({
        supplier_code: supplier.supplier_code || '',
        company_name: supplier.company_name || '',
        vat_number: supplier.vat_number || '',
        fiscal_code: supplier.fiscal_code || '',
        address: supplier.address || '',
        city: supplier.city || '',
        postal_code: supplier.postal_code || '',
        province: supplier.province || '',
        phone: supplier.phone || '',
        mobile: supplier.mobile || '',
        email: supplier.email || '',
        pec: supplier.pec || '',
        sdi_code: supplier.sdi_code || '',
        contact_person: supplier.contact_person || '',
        payment_terms: supplier.payment_terms || '',
        payment_days: supplier.payment_days || 30,
        iban: supplier.iban || '',
        bank_name: supplier.bank_name || '',
        contract_start_date: supplier.contract_start_date ? new Date(supplier.contract_start_date) : null,
        contract_end_date: supplier.contract_end_date ? new Date(supplier.contract_end_date) : null,
        current_price_per_liter: supplier.current_price_per_liter || '',
        notes: supplier.notes || '',
        status: supplier.status || 'active'
      });
      setError(null);
    }
  }, [supplier, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = () => {
    if (!formData.company_name) {
      setError('Ragione sociale obbligatoria');
      return;
    }

    const formatDate = (date) => date ? date.toISOString().split('T')[0] : null;

    onSubmit({
      ...formData,
      contract_start_date: formatDate(formData.contract_start_date),
      contract_end_date: formatDate(formData.contract_end_date)
    });
  };

  if (!supplier) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>Modifica Fornitore: {supplier.company_name}</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
          {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Dati principali */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                Dati Azienda
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Codice Fornitore"
                name="supplier_code"
                value={formData.supplier_code}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Ragione Sociale"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Stato</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Stato"
                >
                  <MenuItem value="active">Attivo</MenuItem>
                  <MenuItem value="inactive">Inattivo</MenuItem>
                  <MenuItem value="suspended">Sospeso</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Partita IVA"
                name="vat_number"
                value={formData.vat_number}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Codice Fiscale"
                name="fiscal_code"
                value={formData.fiscal_code}
                onChange={handleChange}
              />
            </Grid>

            {/* Indirizzo */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom sx={{ mt: 2 }}>
                Indirizzo
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Indirizzo"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Città"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={6} md={1.5}>
              <TextField
                fullWidth
                label="CAP"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={6} md={1.5}>
              <TextField
                fullWidth
                label="Provincia"
                name="province"
                value={formData.province}
                onChange={handleChange}
                inputProps={{ maxLength: 2 }}
              />
            </Grid>

            {/* Contatti */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom sx={{ mt: 2 }}>
                Contatti
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="fornitore@email.com"
                helperText="Email per invio ordini GPL"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="PEC"
                name="pec"
                type="email"
                value={formData.pec}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Telefono"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Cellulare"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Referente"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
              />
            </Grid>

            {/* Dati Fiscali */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom sx={{ mt: 2 }}>
                Dati Fiscali e Pagamento
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Codice SDI"
                name="sdi_code"
                value={formData.sdi_code}
                onChange={handleChange}
                inputProps={{ maxLength: 7 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Termini Pagamento"
                name="payment_terms"
                value={formData.payment_terms}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Giorni Pagamento"
                name="payment_days"
                type="number"
                value={formData.payment_days}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="IBAN"
                name="iban"
                value={formData.iban}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Banca"
                name="bank_name"
                value={formData.bank_name}
                onChange={handleChange}
              />
            </Grid>

            {/* Contratto */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom sx={{ mt: 2 }}>
                Contratto
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <DatePicker
                label="Data Inizio Contratto"
                value={formData.contract_start_date}
                onChange={(date) => setFormData(prev => ({ ...prev, contract_start_date: date }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <DatePicker
                label="Data Fine Contratto"
                value={formData.contract_end_date}
                onChange={(date) => setFormData(prev => ({ ...prev, contract_end_date: date }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Prezzo per Litro (€)"
                name="current_price_per_liter"
                type="number"
                value={formData.current_price_per_liter}
                onChange={handleChange}
                inputProps={{ step: 0.001 }}
              />
            </Grid>

            {/* Note */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Note"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annulla
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Salvataggio...' : 'Salva Modifiche'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSupplierModal;
